/**
 * Loads nonessential trackers, and only ever after the matching consent exists.
 *
 * The rule this module enforces: a tracker's script is not fetched at all until its
 * category is granted. Vendor "consent mode" flags are a second layer, not the first —
 * they reduce what a loaded script does, but the script has already contacted the vendor
 * by then. Not loading is the only way a visitor's data is genuinely not transmitted.
 *
 * Nothing here runs on import. Call applyConsent() with an effective consent object.
 */

import { getEffectiveConsent } from "./consent";

const GA_ID = "G-ZTD37M5TXX";
const META_PIXEL_ID = "1666915858025590";

const loaded = { analytics: false, advertising: false };

function loadScriptOnce({ id, src, async = true }) {
  if (typeof document === "undefined") return;
  if (id && document.getElementById(id)) return;
  const s = document.createElement("script");
  if (id) s.id = id;
  s.async = async;
  s.src = src;
  document.head.appendChild(s);
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
}

function initGA() {
  if (loaded.analytics) return;
  loaded.analytics = true;

  ensureGtagStub();

  // Consent Mode v2 defaults, queued before gtag.js loads so the library sees a
  // denied baseline and grants only what was actually chosen. Belt and braces:
  // we only reach this function when analytics is already granted.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });

  loadScriptOnce({
    id: "tbb-gtag",
    src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
    async: true,
  });

  window.gtag("js", new Date());
  window.gtag("config", GA_ID);
}

function initMetaPixel() {
  if (loaded.advertising) return;
  loaded.advertising = true;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );
  /* eslint-enable */

  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

/**
 * Apply a consent decision. Grants load their trackers; denials are a no-op here
 * because nothing was loaded in the first place.
 *
 * Safe to call repeatedly — each tracker loads at most once per page.
 */
export function applyConsent(consent = getEffectiveConsent()) {
  if (consent?.analytics) initGA();
  if (consent?.advertising) initMetaPixel();
}

/**
 * Tell already-loaded vendor scripts to stop, for a mid-session downgrade.
 *
 * A script cannot be unloaded from a live page, so callers should reload after
 * this. Signalling revocation first means the vendors stop writing identifiers
 * during the moments before that reload happens.
 */
export function revokeLoadedTrackers() {
  try {
    window.fbq?.("consent", "revoke");
  } catch {
    // no-op
  }

  try {
    window.gtag?.("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  } catch {
    // no-op
  }
}

/** True when the Meta Pixel is loaded and permitted to record an event. */
export function advertisingActive() {
  return getEffectiveConsent().advertising === true && typeof window.fbq === "function";
}
